import { useGetOrganizationApps, useUserActiveOrganization } from "@/api";
import { QuickStartLink } from "@/components/apps/QuickStartLink";
import { TypographyBase } from "@/components/typography/Base";
import { TypographyH3 } from "@/components/typography/H3";
import { TypographySM } from "@/components/typography/SM";
import TooltipHover from "@/components/ui/TooltipHover";
import Alert from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LoadingCircle } from "@/components/ui/loadingCircle";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { PaymentSection } from "@/features/app-subscription-payment";
import { addDays, format } from "date-fns";
import { Asterisk, Info } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { useHandleSubmitSubscriptionOffer } from "../hooks/handleSubmit";

const formatDate = (date: Date) => format(date, "yyyy-MM-dd");

const CreateSubscriptionOfferForm = () => {
  const [disableSubmit, setDisableSubmit] = useState(true);
  const [paymentEnabled, setPaymentEnabled] = useState(false);
  const { activeOrganization } = useUserActiveOrganization();
  const formRef = useRef<HTMLFormElement>(null);
  const today = useMemo(() => new Date(), []);

  const {
    data: apps,
    loading: appsLoading,
    error
  } = useGetOrganizationApps({
    organizationSlug: activeOrganization?.slug || "",
    organizationAppsOnly: true
  });

  const { handleSubmit, loading: submitLoading } =
    useHandleSubmitSubscriptionOffer(activeOrganization);

  const checkValidity = (form: HTMLFormElement) => {
    const formData = new FormData(form);

    if (formData.get("payment-toggle") === "on" && !formData.has("price")) {
      return setDisableSubmit(true);
    }

    return setDisableSubmit(!form.checkValidity());
  };

  const disableInput = !!error || apps.length < 1;

  return (
    <div>
      <TypographyH3>Create subscription offer</TypographyH3>
      <TypographySM className="dark:text-brand-gray-300">
        Details on the offer you are providing to your customer.
      </TypographySM>

      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-4 mt-8"
        onChange={(e) => checkValidity(e.currentTarget)}
        ref={formRef}
      >
        <TypographyBase className="py-1 border-b dark:border-brand-gray-600">
          App information
        </TypographyBase>
        <div className="grid grid-cols-[17ch_1fr]">
          <Label className="pt-[11px]" htmlFor="app">
            Application
            <Asterisk className="inline w-3.5 h-3.5" />
          </Label>
          {error && (
            <Alert
              title="Unable to load apps"
              description="Please try to reload the page or report the issue."
              variant="destructive"
              data-testid="loadAppErrorAlert"
            />
          )}
          {!error && apps.length < 1 && (
            <div className="flex flex-col gap-2 items-end">
              <Alert
                title="No apps found"
                description="It looks like your organization does not have any apps yet. Get started by deploying one with Numerous."
                data-testid="noAppsFoundAlert"
              />
              <QuickStartLink data-testid="noAppsInOrganizationCardLink" />
            </div>
          )}
          {!error && apps.length > 0 && (
            <Select name="app" required>
              <SelectTrigger disabled={appsLoading} data-testid="appSelect">
                {appsLoading ? (
                  <LoadingCircle />
                ) : (
                  <SelectValue placeholder="Select an app" />
                )}
              </SelectTrigger>
              <SelectContent>
                {apps.map((app) => (
                  <SelectItem
                    key={app.id}
                    value={app.slug}
                    data-testid="appItem"
                  >
                    {app.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <TypographyBase className="py-1 border-b dark:border-brand-gray-600">
          Offer details
        </TypographyBase>
        <div className="grid grid-cols-[17ch_1fr] gap-y-4">
          <Label className="pt-[11px]" htmlFor="email">
            Customer email
            <Asterisk className="inline w-3.5 h-3.5" />
          </Label>
          <Input
            name="email"
            type="email"
            data-testid="emailInput"
            required
            disabled={disableInput}
            placeholder="Customer email"
          />

          <Label className="pt-[11px]" htmlFor="message">
            Message
          </Label>
          <Textarea
            name="message"
            placeholder="Offer message"
            className="h-32"
            disabled={disableInput}
            maxLength={300}
          />

          <Label className="pt-[11px]" htmlFor="reference">
            Internal reference
            <TooltipHover text="This is a reference for your internal use. It will not be shown to the customer.">
              <Info size={18} className="relative ml-2 top-1" />
            </TooltipHover>
          </Label>
          <Input
            name="reference"
            placeholder="Internal reference"
            disabled={disableInput}
          />
          <Label className="pt-[11px]" htmlFor="expiration">
            Offer Expiry Date
          </Label>
          <Input
            name="expires-at"
            type="date"
            min={formatDate(addDays(today, 1))}
            defaultValue={formatDate(addDays(today, 30))}
            disabled={disableInput}
          />
        </div>
        <div className="flex items-center justify-between py-1 border-b dark:border-brand-gray-600">
          <TypographyBase className="cursor-default">
            {paymentEnabled ? "Paid Subscription" : "Free Subscription"}
          </TypographyBase>
          <div className="flex items-center gap-2">
            <Label htmlFor="payment-toggle">
              {paymentEnabled ? "Disable payment" : "Enable payment"}
            </Label>
            <Switch
              id="payment-toggle"
              name="payment-toggle"
              data-testid="payment-toggle"
              checked={paymentEnabled}
              onCheckedChange={setPaymentEnabled}
            />
          </div>
        </div>
        {activeOrganization && paymentEnabled && (
          <PaymentSection
            organization={activeOrganization}
            checkFormValidity={() =>
              formRef.current && checkValidity(formRef.current)
            }
          />
        )}
        <div className="flex justify-end items-center gap-3">
          {submitLoading && <LoadingCircle type="default" />}
          <Button
            variant="default"
            type="submit"
            disabled={disableSubmit}
            data-testid="submitButton"
          >
            Send offer
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateSubscriptionOfferForm;
